import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { projectThemes } from './themes';

declare global {
  interface Window {
    __BLOG_POST_DATA__?: {
      project: string;
      post: any;
    };
  }
}

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  date: string;
  excerpt: string;
  content: string;
  author?: string;
  category?: string;
  readingTime?: string;
  seoTitle?: string;
  seoDescription?: string;
  keywords?: string;
}

export default function BlogPost({ project }: { project: string }) {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const theme = projectThemes[project] || projectThemes['tattvadynamics'];

  useEffect(() => {
    if (window.__BLOG_POST_DATA__ && 
        window.__BLOG_POST_DATA__.project === project && 
        window.__BLOG_POST_DATA__.post.slug === slug) {
      setPost(window.__BLOG_POST_DATA__.post);
      setLoading(false);
      return;
    }

    const loadPost = async () => {
      try {
        const response = await fetch(`/content/blogs/${project}.json`);
        const list = await response.json();
        const foundPost = list.find((p: BlogPost) => p.slug === slug);
        setPost(foundPost || null);
      } catch (e) {
        console.error("Failed to load post", e);
      } finally {
        setLoading(false);
      }
    };
    loadPost();
    window.scrollTo(0, 0);
  }, [project, slug]);

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="animate-spin rounded-full h-4 w-4 border-t border-white/20"></div>
    </div>
  );

  if (!post) return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-4">
      <h1 className="text-2xl font-bold mb-4">Story not found</h1>
      <Link to="/blog" className="text-sm uppercase tracking-widest opacity-50 hover:opacity-100 transition-opacity">Back to blog</Link>
    </div>
  );

  const authorName = post.author || theme.bloggedBy;
  const authorInitials = authorName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-black text-white" style={{ fontFamily: theme.fontFamily }}>
      <style>{`
        ::selection {
          background-color: ${theme.primary}44;
          color: white;
        }
      `}</style>
      <Helmet>
        <title>{post.seoTitle || post.title} | {theme.blogName}</title>
        <meta name="description" content={post.seoDescription || post.excerpt} />
        <link rel="icon" type="image/x-icon" href={theme.favicon} />
      </Helmet>

      {/* Modern Navigation */}
      <nav className="sticky top-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/blog" className="flex items-center gap-3 group">
            <img className="h-8 w-auto transition-all" src={theme.logo} alt="" />
            <span className="text-sm font-bold tracking-widest opacity-70 group-hover:opacity-100">
               {theme.brandText}
            </span>
          </Link>
          <Link to="/" className="text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-2 rounded-full border border-white/10 hover:bg-white hover:text-black transition-all">
             Home
          </Link>
        </div>
      </nav>

      <article className="max-w-4xl mx-auto px-6 py-20 md:py-32">
        <motion.header 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16"
        >
          <div className="flex items-center gap-3 mb-8">
             <span className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: theme.primary }}>
               {post.category || 'Insights'}
             </span>
             <span className="h-1 w-1 rounded-full bg-white/20"></span>
             <span className="text-xs text-gray-500">{post.readingTime || '5 min read'}</span>
          </div>

          <h1 className="text-4xl md:text-6xl font-bold mb-10 tracking-tight leading-[1.1]">
            {post.title}
          </h1>

          <div className="flex items-center gap-4 py-8 border-y border-white/5">
             <div className="h-10 w-10 rounded-full bg-white/5 flex items-center justify-center border border-white/10 text-xs font-bold tracking-tighter" style={{ borderColor: `${theme.primary}33`, color: theme.primary }}>
                {authorInitials}
             </div>
             <div>
                <p className="text-[10px] font-bold uppercase tracking-widest opacity-40 mb-1">By</p>
                <p className="text-sm font-semibold" style={{ color: theme.primary }}>{authorName}</p>
                <p className="text-xs text-gray-500">{post.date}</p>
             </div>
          </div>
        </motion.header>

        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="prose prose-invert prose-primary max-w-none"
        >
          <div className="text-gray-300 leading-[1.8] text-lg md:text-xl space-y-10">
            {post.content.split('\n').map((paragraph, i) => {
              if (paragraph.startsWith('###')) {
                return <h3 key={i} className="text-2xl md:text-3xl font-bold text-white pt-6" style={{ color: theme.primary }}>{paragraph.replace('###', '')}</h3>;
              }
              return <p key={i}>{paragraph}</p>;
            })}
          </div>
        </motion.div>

        <footer className="mt-32 pt-12 border-t border-white/5">
          <div className="bg-white/[0.02] border border-white/5 rounded-3xl p-8 md:p-12 text-center" style={{ borderColor: `${theme.primary}22` }}>
             <h4 className="text-xl font-bold mb-4">Thanks for reading</h4>
             <p className="text-gray-400 mb-8 max-w-xl mx-auto">Stay updated with our latest engineering insights and product news from the <span style={{ color: theme.primary }}>{project}</span> team.</p>
             <Link 
               to="/blog" 
               className="inline-block px-8 py-3 rounded-full text-xs font-bold uppercase tracking-widest hover:scale-105 transition-transform"
               style={{ backgroundColor: theme.primary, color: 'white' }}
             >
               Explore more stories
             </Link>
          </div>
        </footer>
      </article>

      <footer className="py-20 border-t border-white/5 text-center px-6 opacity-40">
          <p className="text-[10px] uppercase tracking-[0.5em]">Essence. Engineered.</p>
      </footer>
    </div>
  );
}
